/**
 * Converts an ISO 8601 time string (assumed to be UTC) into localized date and time strings
 * for a specified timezone and locale.
 *
 * @param {string} isoTimeString The ISO 8601 formatted date and time string (e.g., '2023-10-27T10:00:00Z').
 * @param {string} [TZ='Europe/London'] The IANA timezone string (e.g., 'America/New_York').
 * @param {string} [locale='en-GB'] The locale string (e.g., 'fr-FR', 'en-US') to be used for formatting.
 * @returns {{
 * formattedDate: string,
 * formattedTime: string,
 * formattedTimeShort: string,
 * timeZoneNameShort: string
 * }} An object containing the formatted date and time strings.
 * - formattedDate: The full date (weekday, day, month, year).
 * - formattedTime: The time with hours, minutes, and a short timezone name.
 * - formattedTimeShort: The time with hours and minutes only (no timezone name).
 * - timeZoneNameShort: The short timezone label for the configured IANA zone (e.g. BRT, GMT).
 */

interface ISimpleDateOptions {
  day: 'numeric' | '2-digit';
  month: 'long' | 'short' | 'narrow' | 'numeric' | '2-digit';
  year: 'numeric' | '2-digit';
  timeZone: string;
}

interface IDateOptions extends ISimpleDateOptions {
  weekday: 'long' | 'short' | 'narrow';
}

interface ITimeShortOptions {
  hour: 'numeric' | '2-digit';
  minute: 'numeric' | '2-digit';
  timeZone: string;
  hour12: boolean;
}

interface ITimeOptions extends ITimeShortOptions {
  timeZoneName:
    | 'short'
    | 'long'
    | 'shortOffset'
    | 'longOffset'
    | 'shortGeneric'
    | 'longGeneric';
}

export default function getLocalTimeData(
  isoTimeString: string,
  TZ = 'Europe/London',
  locale = 'en-GB'
) {
  const utcDate = new Date(isoTimeString);

  const optionsTime: ITimeOptions = {
    hour: 'numeric',
    minute: 'numeric',
    timeZone: TZ,
    timeZoneName: 'short',
    hour12: true,
  };

  const optionsTimeShort: ITimeShortOptions = {
    hour: 'numeric',
    minute: 'numeric',
    timeZone: TZ,
    hour12: true,
  };

  const simpleOptionsDate: ISimpleDateOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: TZ,
  };

  const optionsDate: IDateOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: TZ,
  };

  const formatterSimpleDate = new Intl.DateTimeFormat(
    locale,
    simpleOptionsDate
  );
  const formatterDate = new Intl.DateTimeFormat(locale, optionsDate);
  const formatterTime = new Intl.DateTimeFormat(locale, optionsTimeShort);
  const formatterTimeShort = new Intl.DateTimeFormat(locale, optionsTime);

  const formattedSimpleDate = formatterSimpleDate.format(utcDate);
  const formattedDate = formatterDate.format(utcDate);
  const formattedTime = formatterTimeShort.format(utcDate);
  const formattedTimeShort = formatterTime.format(utcDate);

  const timeZoneNameShort =
    formatterTimeShort
      .formatToParts(utcDate)
      .find(({ type }) => type === 'timeZoneName')?.value ?? '';

  return {
    formattedSimpleDate,
    formattedDate,
    formattedTime,
    formattedTimeShort,
    timeZoneNameShort,
  };
}
